import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Title, TextInput, Button, Group, Stack, ActionIcon, NumberInput, Text, Paper, Select, Center } from '@mantine/core';
import { ArrowLeft, Plus, Trash, Save } from 'lucide-react';
import { noteToMidi, getKoreanNoteName } from '../utils/musicUtils';

const API_URL = process.env.REACT_APP_API_URL || '';

// 한글 음계와 영문 매핑 데이터
const NOTE_OPTIONS = [
    { value: 'C', label: '도' },
    { value: 'C#', label: '도#' },
    { value: 'D', label: '레' },
    { value: 'D#', label: '레#' },
    { value: 'E', label: '미' },
    { value: 'F', label: '파' },
    { value: 'F#', label: '파#' },
    { value: 'G', label: '솔' },
    { value: 'G#', label: '솔#' },
    { value: 'A', label: '라' },
    { value: 'A#', label: '라#' },
    { value: 'B', label: '시' },
];

export default function SongCreationPage() {
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [artist, setArtist] = useState('');
    const [coverUrl, setCoverUrl] = useState('');
    const [notes, setNotes] = useState([{ lyric: '', note: 'C', octave: 4 }]);
    
    // 키보드 내비게이션을 위한 ref 관리
    const inputRefs = useRef([]);

    const handleAddNote = () => {
        setNotes([...notes, { lyric: '', note: 'C', octave: 4 }]);
    };

    const handleRemoveNote = (index) => {
        setNotes(notes.filter((_, i) => i !== index));
    };

    const handleNoteChange = (index, field, value) => {
        const newNotes = [...notes];
        newNotes[index][field] = value;
        setNotes(newNotes);
    };

    // 키보드 화살표 이동 로직
    const handleKeyDown = (e, index, field) => {
        const rowCount = notes.length;
        if (e.key === 'ArrowDown' && index < rowCount - 1) {
            inputRefs.current[`${index + 1}-${field}`]?.focus();
        } else if (e.key === 'ArrowUp' && index > 0) {
            inputRefs.current[`${index - 1}-${field}`]?.focus();
        } else if (e.key === 'ArrowRight' && field === 'lyric') {
            inputRefs.current[`${index}-note`]?.focus();
        } else if (e.key === 'ArrowLeft' && field === 'note') {
            inputRefs.current[`${index}-lyric`]?.focus();
        }
    };

    const handleSave = async () => {
        if (!title || !artist || notes.length === 0) {
            alert('모든 필드를 입력해주세요.');
            return;
        }

        const songData = {
            title,
            artist,
            cover_url: coverUrl || 'https://images.unsplash.com/photo-1516280440614-6697288d5d38?auto=format&fit=crop&q=80&w=1000',
            difficulty: 'Custom',
            full_lyrics: notes.map(n => n.lyric).join(''),
            notes: notes.map((n) => ({
                pitch: noteToMidi(`${n.note}${n.octave}`),
                lyric: n.lyric
            }))
        };

        try {
            const response = await fetch(`${API_URL}/api/songs`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(songData),
            });
            if (!response.ok) throw new Error('노래 저장에 실패했습니다.');
            const result = await response.json();
            if (result.success) {
                alert('성공적으로 저장되었습니다!');
                navigate('/');
            }
        } catch (error) {
            alert('저장 중 오류가 발생했습니다: ' + error.message);
        }
    };

    return (
        <Container size="md" py="xl">
            {/* 상단 헤더: 버튼들을 위로 배치 */}
            <Group mb="xl">
                <ActionIcon variant="subtle" onClick={() => navigate('/')} size="lg">
                    <ArrowLeft />
                </ActionIcon>
                <Title order={2}>Create New Song</Title>
            </Group>

            <Paper withBorder p="md" mb="xl">
                <Stack>
                    <TextInput
                        label="Song Title"
                        placeholder="노래 제목을 입력하세요"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                    />
                    <TextInput
                        label="Artist"
                        placeholder="아티스트를 입력하세요"
                        value={artist}
                        onChange={(e) => setArtist(e.target.value)}
                        required
                    />
                    <TextInput
                        label="Cover Image URL"
                        placeholder="https://example.com/image.jpg (비워두면 기본 이미지 적용)"
                        value={coverUrl}
                        onChange={(e) => setCoverUrl(e.target.value)}
                    />
                </Stack>
            </Paper>

            <Group justify="space-between" align="flex-end" mb="md">
                <Title order={3}>Notes Sequence</Title>
                
                {/* 버튼들을 라벨 옆/아래 쪽으로 배치 */}
                <Group gap="xs">
                    <Button 
                        leftSection={<Plus size={16} />} 
                        variant="light" 
                        color="blue"
                        onClick={handleAddNote}
                    >
                        Add Note
                    </Button>
                    <Button 
                        leftSection={<Save size={16} />} 
                        color="green" 
                        onClick={handleSave}
                    >
                        Save Song
                    </Button>
                </Group>
            </Group>
            <Stack mb="xl">
                {notes.map((note, index) => (
                    <Paper key={index} withBorder p="sm" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                        <Text fw={700} w={30} size="sm">#{index + 1}</Text>
                        
                        {/* 가사 입력 */}
                        <TextInput
                            placeholder="가사"
                            value={note.lyric}
                            onChange={(e) => handleNoteChange(index, 'lyric', e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'lyric')}
                            ref={el => inputRefs.current[`${index}-lyric`] = el}
                            style={{ flex: 1 }}
                        />

                        {/* 음계 선택 (한글 콤보박스) */}
                        <Select
                            placeholder="음 선택"
                            data={NOTE_OPTIONS}
                            value={note.note}
                            onChange={(val) => handleNoteChange(index, 'note', val)}
                            onKeyDown={(e) => handleKeyDown(e, index, 'note')}
                            ref={(el) => { inputRefs.current[`${index}-note`] = el; }}
                            w={100}
                        />                        

                        {/* 옥타브 입력 */}
                        <NumberInput
                            value={note.octave}
                            onChange={(val) => handleNoteChange(index, 'octave', val)}
                            min={2}
                            max={7}
                            w={65}
                        />

                        {/* 영문 음표 표시 (C4 형식) */}
                        <Paper withBorder px="xs" py={5} bg="gray.0" w={60}>
                            <Center>
                                <Text size="xs" fw={700} c="blue">{note.note}{note.octave}</Text>
                            </Center>
                        </Paper>

                        {/* 삭제 버튼 */}
                        <ActionIcon color="red" variant="subtle" onClick={() => handleRemoveNote(index)}>
                            <Trash size={18} />
                        </ActionIcon>
                    </Paper>
                ))}
            </Stack>

            {/* 하단에도 추가 버튼 하나 더 배치 (편의용) */}
            <Button fullWidth leftSection={<Plus />} variant="light" onClick={handleAddNote} mt="md">
                Add More Note
            </Button>
        </Container>
    );
}